import '@pocket/web-ui/lib/pocket-web-ui.css'
import { useEffect } from 'react'
import React from 'react'

import withRedux from 'next-redux-wrapper'
import withReduxSaga from 'next-redux-saga'
import { Provider } from 'react-redux'
import { initializeStore } from 'store'
import { ViewportProvider } from '@pocket/web-ui'

import * as Sentry from '@sentry/node'
import { sentrySettings } from 'common/utilities/sentry-settings'

import { fetchUserData, userHydrate } from 'connectors/user/user.state'
import { signalTestsReady } from '../../cypress/support/utils'
import { checkSessGuid, sessGuidHydrate } from 'connectors/user/user.state'

import { featuresHydrate } from 'connectors/feature-flags/feature-flags.state'
import { fetchUnleashData } from 'connectors/feature-flags/feature-flags.state'

import { loadPolyfills } from 'common/utilities/polyfills'
import { appWithTranslation } from 'common/utilities/i18n'

import { DevTools } from 'components/dev-tools/dev-tools'
import DiscoverError from './_error'

import { PostTrustInit } from 'connectors/one-trust/one-trust'

const showDevTools = process.env.SHOW_DEV === 'included'

/** Set up Sentry so we may catch errors
 --------------------------------------------------------------- */
Sentry.init(sentrySettings)

/** App
 --------------------------------------------------------------- */
function DiscoverApp({ Component, pageProps, store, err }) {
  const { path } = pageProps

  useEffect(() => {
    // Load any relevant polyfills
    loadPolyfills()

    // signal to Cypress that React client side has loaded
    signalTestsReady()
  }, [])

  // Render our page with a wrapped provider and optional devTools
  return (
    <Provider store={store}>
      <PostTrustInit path={path} />
      <ViewportProvider>
        {showDevTools ? <DevTools /> : null}
        <Component {...pageProps} err={err} />
      </ViewportProvider>
    </Provider>
  )
}

/** Initial props runs only on the server side
 * NOTE: This section is run in node so we can assume a lack of
 * document, window, and other browser conventions
 --------------------------------------------------------------- */
DiscoverApp.getInitialProps = async ({ Component, ctx }) => {
  // If we are rendering the error we don't want to run through hydration
  if (Component === DiscoverError) return { pageProps: {} }

  // Get the host, url and path so they are available to all pages
  const { req, res, store } = ctx
  const url = req.protocol + '://' + req.get('host') + req.originalUrl
  const path = req.originalUrl // req.originalUrl maintains query params

  // The following functions are deemed critical, render blocking requests
  // ---------------------------------------------------------------

  // On SSR requests we need to pass through cookies explicitly
  const cookie = ctx?.req?.headers?.cookie

  // !! Hydrate user
  const user = await fetchUserData(cookie)
  if (user) store.dispatch(userHydrate(user))

  // !! Hydrate sess_guid
  const sessGuid = await checkSessGuid(cookie, res)
  if (sessGuid) store.dispatch(sessGuidHydrate(sessGuid))

  // !! Hydrate features set with unleash
  const features = await fetchUnleashData(user, sessGuid)
  if (features) store.dispatch(featuresHydrate(features))

  // Wait for getInitialProps to run the component if they exist
  const pageProps = Component.getInitialProps
    ? await Component.getInitialProps(ctx, user, sessGuid, features)
    : {}

  // Return our modified pageProps
  return { pageProps: { ...pageProps, url, path } }
}

/**
 * Export the app.  This wraps the app with a few things:
 * 1. Redux: for managing state
 * 2. ReduxSaga: for managing async state requirements
 * 3. i18Next: for managing localizations
 */
export default withRedux(initializeStore)(
  withReduxSaga(appWithTranslation(DiscoverApp))
)
